import { ZButton } from "@/shared/components/button";
import { Modal, ModalContent } from "@nextui-org/react";
import { Check } from "lucide-react";
import type { FC } from "react";

interface ProceedConfirmationModalProps {
  /**
   * Whether the modal is open or not
   */
  isOpen: boolean;

  /**
   * Handler called when the modal is closed
   */
  onClose: () => void;

  /**
   * Handler called when user confirms to proceed
   */
  onConfirm: () => void;

  /**
   * Current progress percentage (0-100)
   */
  progressPercentage: number;

  /**
   * Whether there are any upload errors
   */
  hasUploadErrors: boolean;

  /**
   * Number of completed required items
   */
  completedItems: number;

  /**
   * Total number of required items
   */
  totalItems: number;
}

const ProceedConfirmationModal: FC<ProceedConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  progressPercentage,
  hasUploadErrors,
  completedItems,
  totalItems,
}) => {
  // Determine if user can proceed (100% complete and no upload errors)
  const canProceed = progressPercentage === 100 && !hasUploadErrors;

  // Calculate items remaining
  const itemsRemaining = totalItems - completedItems;

  // Determine which step is current
  const isStep1Complete = progressPercentage === 100;

  // Get status message
 

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={true}
      size="lg"
    >
      <ModalContent className="max-w-[512px] shadow-[0px_0px_24px_rgba(0,0,0,0.05)] rounded-[16px] p-6">
        {/* Main Container */}
        <div className="relative flex flex-col items-center p-0 w-full">
          {/* Content Section */}
          <div className="flex flex-col items-start gap-6 w-full">
            {/* Title and Description */}
            <div className="flex flex-col items-center gap-2 w-full">
              <h2 className="text-[18px] font-semibold leading-[28px] text-center text-[#11181C] w-full">
                Complete Required Items
              </h2>
              <p className="text-[14px] font-normal leading-[20px] text-center text-[#3F3F46] w-full">
                You still need to finish mandatory questions and required documents before continuing to automated review.
              </p>
            </div>

            {/* Process Steps Container */}
            <div className="flex flex-col items-start py-5 px-4 gap-3 w-full bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg">
              {/* Step 1 - Complete Mandatory Requirements */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center gap-3 flex-shrink-0">
                  {/* Circle Indicator */}
                  <div
                    className={`w-5 h-5 rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] flex items-center justify-center ${
                      isStep1Complete
                        ? 'bg-[#E8FAF0] border border-[#17C964]'
                        : 'bg-[#FEE7EF] border border-[#F31260]'
                    }`}
                  >
                    {isStep1Complete && (
                      <Check className="w-3 h-3 text-[#17C964]" strokeWidth={3} />
                    )}
                  </div>
                  {/* Connecting Line */}
                  <div className={`w-px flex-1 min-h-[44px] ${
                    isStep1Complete ? 'bg-[#17C964]' : 'bg-[#71717A]'
                  }`} />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start pb-3 gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Complete Mandatory Requirements
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    Respond to the questions and upload documents requested.
                  </p>
                </div>
              </div>

              {/* Step 2 - Proceed to Automated Review */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center gap-3 flex-shrink-0">
                  {/* Circle Indicator */}
                  <div className={`w-5 h-5 rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] ${
                    isStep1Complete
                      ? 'bg-[#E6F1FE] border border-[#006FEE]'
                      : 'bg-white border border-[#71717A]'
                  }`} />
                  {/* Connecting Line */}
                  <div className="w-px flex-1 bg-[#71717A] min-h-[44px]" />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start pb-3 gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Proceed to Automated Review
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    AI will evaluate your response to generate follow-up questions.
                  </p>
                </div>
              </div>

              {/* Step 3 - Answer Follow-up Questions & Submit */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center flex-shrink-0">
                  {/* Circle Indicator */}
                  <div className="w-5 h-5 bg-white border border-[#71717A] rounded-full" />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Answer Follow-up Questions & Submit
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    Review and answer follow-up questions, and submit your final responses.
                  </p>
                </div>
              </div>
            </div>


            {/* Action Button */}
            <ZButton
              className="w-full h-10 px-4 py-2 text-[16px] font-medium leading-[24px] bg-[#006FEE] text-white rounded-md"
              onClick={onConfirm}
              isDisabled={!canProceed}
            >
              Confirm & Proceed
            </ZButton>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ProceedConfirmationModal;
